import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreateDocumentType,
  CreateVoucherBook,
  DocumentType,
  UpdateDocumentType,
  UpdateVoucherBook,
  VoucherBook,
  VoucherBookWithCounter,
} from '@dcc-erp/shared';

import { api, apiJson } from '../api';

const docTypeKey = (companyId: string | undefined) => ['document-types', companyId] as const;

const voucherBookKey = (companyId: string | undefined, typeId: string | undefined) =>
  ['voucher-books', companyId, typeId] as const;

export function useDocumentTypes(companyId: string | undefined, activeOnly = true) {
  return useQuery({
    queryKey: [...docTypeKey(companyId), { activeOnly }],
    queryFn: () =>
      api<DocumentType[]>(`/companies/${companyId}/document-types?activeOnly=${activeOnly}`),
    enabled: !!companyId,
  });
}

export function useDocumentType(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['document-types', companyId, id],
    queryFn: () => api<DocumentType>(`/companies/${companyId}/document-types/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useCreateDocumentType(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreateDocumentType, 'companyId'>) =>
      apiJson<typeof input, DocumentType>('POST', `/companies/${companyId}/document-types`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: docTypeKey(companyId) }),
  });
}

export function useUpdateDocumentType(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDocumentType }) =>
      apiJson<UpdateDocumentType, DocumentType>(
        'PATCH',
        `/companies/${companyId}/document-types/${id}`,
        data,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: docTypeKey(companyId) }),
  });
}

export function useDeleteDocumentType(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, DocumentType>('DELETE', `/companies/${companyId}/document-types/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: docTypeKey(companyId) }),
  });
}

export function useVoucherBooks(
  companyId: string | undefined,
  typeId: string | undefined,
  activeOnly = true,
) {
  return useQuery({
    queryKey: [...voucherBookKey(companyId, typeId), { activeOnly }],
    queryFn: () =>
      api<VoucherBookWithCounter[]>(
        `/companies/${companyId}/document-types/${typeId}/books?activeOnly=${activeOnly}`,
      ),
    enabled: !!companyId && !!typeId,
  });
}

export function useVoucherBook(
  companyId: string | undefined,
  typeId: string | undefined,
  bookId: string | undefined,
) {
  return useQuery({
    queryKey: ['voucher-books', companyId, typeId, bookId],
    queryFn: () =>
      api<VoucherBook>(`/companies/${companyId}/document-types/${typeId}/books/${bookId}`),
    enabled: !!companyId && !!typeId && !!bookId,
  });
}

export function useCreateVoucherBook(companyId: string | undefined, typeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreateVoucherBook, 'docTypeId'>) =>
      apiJson<typeof input, VoucherBook>(
        'POST',
        `/companies/${companyId}/document-types/${typeId}/books`,
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: voucherBookKey(companyId, typeId) }),
  });
}

export function useUpdateVoucherBook(companyId: string | undefined, typeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bookId, data }: { bookId: string; data: UpdateVoucherBook }) =>
      apiJson<UpdateVoucherBook, VoucherBook>(
        'PATCH',
        `/companies/${companyId}/document-types/${typeId}/books/${bookId}`,
        data,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: voucherBookKey(companyId, typeId) }),
  });
}

export function useDeleteVoucherBook(companyId: string | undefined, typeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bookId: string) =>
      apiJson<undefined, VoucherBook>(
        'DELETE',
        `/companies/${companyId}/document-types/${typeId}/books/${bookId}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: voucherBookKey(companyId, typeId) }),
  });
}
